import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import StatusBadge from '../components/common/StatusBadge';
import { ShieldCheck, Truck, AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VERDICT = {
  OK: { tone: 'success', label: 'ELIGIBLE', Icon: CheckCircle2 },
  VERIFICATION_REQUIRED: { tone: 'warning', label: 'VERIFICATION REQUIRED', Icon: HelpCircle },
  BLOCKED: { tone: 'danger', label: 'BLOCKED', Icon: AlertTriangle },
};
const CHECK_TONE = { OK: 'success', VERIFICATION_REQUIRED: 'warning', BLOCKED: 'danger' };

const numOrNull = (v) => (v === '' || v === null || v === undefined) ? null : Number(v);
const triOrNull = (v) => (v === '' ? null : v === 'yes');

const Num = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs text-gray-500">{label}</span>
    <input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="unknown" />
  </label>
);
const Tri = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs text-gray-500">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
      <option value="">Unknown</option><option value="yes">Yes</option><option value="no">No</option>
    </select>
  </label>
);

export default function CapabilityCheck() {
  const { token } = useAuth();
  const [eq, setEq] = useState({ payload_lbs: '', towing_capacity_lbs: '', gvwr_lbs: '', hitch_rating_lbs: '', trailer_gvwr_lbs: '', bed_length_in: '', has_trailer: '', cdl_class: '', authority: '', insurance_active: '', driver_qualified: '' });
  const [ld, setLd] = useState({ haul_mode: 'carry', cargo_weight_lbs: '', cargo_length_in: '', trailer_weight_lbs: '', requires_cdl: '', requires_authority: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const setE = (k) => (v) => setEq((s) => ({ ...s, [k]: v }));
  const setL = (k) => (v) => setLd((s) => ({ ...s, [k]: v }));

  const run = async () => {
    setLoading(true); setResult(null);
    const payload = {
      equipment: {
        payload_lbs: numOrNull(eq.payload_lbs), towing_capacity_lbs: numOrNull(eq.towing_capacity_lbs),
        gvwr_lbs: numOrNull(eq.gvwr_lbs), hitch_rating_lbs: numOrNull(eq.hitch_rating_lbs),
        trailer_gvwr_lbs: numOrNull(eq.trailer_gvwr_lbs), bed_length_in: numOrNull(eq.bed_length_in),
        has_trailer: triOrNull(eq.has_trailer), cdl_class: eq.cdl_class || null,
        authority: triOrNull(eq.authority), insurance_active: triOrNull(eq.insurance_active),
        driver_qualified: triOrNull(eq.driver_qualified),
      },
      load: {
        haul_mode: ld.haul_mode, cargo_weight_lbs: numOrNull(ld.cargo_weight_lbs),
        cargo_length_in: numOrNull(ld.cargo_length_in), trailer_weight_lbs: numOrNull(ld.trailer_weight_lbs),
        requires_cdl: triOrNull(ld.requires_cdl), requires_authority: triOrNull(ld.requires_authority),
      },
    };
    try {
      const res = await fetch(`${API_URL}/api/loads/match-capability`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      setResult(await res.json());
    } finally { setLoading(false); }
  };

  const v = result ? (VERDICT[result.verdict] || VERDICT.VERIFICATION_REQUIRED) : null;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-6" data-testid="capability-check">
        <Breadcrumb items={[{ label: 'Equipment Capability Check' }]} />
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-blue-700" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Equipment Capability Check</h1>
            <p className="text-sm text-gray-500 mt-0.5">Deterministic pre-assignment safety check. Payload and towing capacity are never interchanged; unknowns require verification.</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2 mt-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="equipment-panel">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Truck className="w-4 h-4 text-gray-500" /> Equipment / Driver</h2>
            <div className="grid grid-cols-2 gap-3">
              <Num label="Payload (lb)" value={eq.payload_lbs} onChange={setE('payload_lbs')} testid="in-payload" />
              <Num label="Towing capacity (lb)" value={eq.towing_capacity_lbs} onChange={setE('towing_capacity_lbs')} testid="in-towing" />
              <Num label="GVWR (lb)" value={eq.gvwr_lbs} onChange={setE('gvwr_lbs')} testid="in-gvwr" />
              <Num label="Hitch rating (lb)" value={eq.hitch_rating_lbs} onChange={setE('hitch_rating_lbs')} testid="in-hitch" />
              <Num label="Trailer GVWR (lb)" value={eq.trailer_gvwr_lbs} onChange={setE('trailer_gvwr_lbs')} testid="in-trailer-gvwr" />
              <Num label="Bed length (in)" value={eq.bed_length_in} onChange={setE('bed_length_in')} testid="in-bed" />
              <Tri label="Has trailer" value={eq.has_trailer} onChange={setE('has_trailer')} testid="in-has-trailer" />
              <label className="block"><span className="text-xs text-gray-500">CDL class</span>
                <select value={eq.cdl_class} onChange={(e) => setE('cdl_class')(e.target.value)} data-testid="in-cdl"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                  <option value="">Unknown</option><option value="A">A</option><option value="B">B</option><option value="C">C</option><option value="none">No CDL</option>
                </select></label>
              <Tri label="Authority" value={eq.authority} onChange={setE('authority')} testid="in-authority" />
              <Tri label="Insurance active" value={eq.insurance_active} onChange={setE('insurance_active')} testid="in-insurance" />
              <Tri label="Driver qualified" value={eq.driver_qualified} onChange={setE('driver_qualified')} testid="in-qualified" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="load-panel">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Load Requirement</h2>
            <div className="grid grid-cols-2 gap-3">
              <label className="block col-span-2"><span className="text-xs text-gray-500">Haul mode</span>
                <select value={ld.haul_mode} onChange={(e) => setL('haul_mode')(e.target.value)} data-testid="in-haul-mode"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                  <option value="carry">Carry (in bed) — uses PAYLOAD</option><option value="tow">Tow (trailered) — uses TOWING CAPACITY</option>
                </select></label>
              <Num label="Cargo weight (lb)" value={ld.cargo_weight_lbs} onChange={setL('cargo_weight_lbs')} testid="in-cargo-weight" />
              <Num label="Cargo length (in)" value={ld.cargo_length_in} onChange={setL('cargo_length_in')} testid="in-cargo-length" />
              <Num label="Empty trailer weight (lb)" value={ld.trailer_weight_lbs} onChange={setL('trailer_weight_lbs')} testid="in-trailer-weight" />
              <Tri label="Requires CDL" value={ld.requires_cdl} onChange={setL('requires_cdl')} testid="in-req-cdl" />
              <Tri label="Requires authority" value={ld.requires_authority} onChange={setL('requires_authority')} testid="in-req-authority" />
            </div>
            <button onClick={run} disabled={loading} data-testid="run-capability"
              className="mt-4 w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {loading ? 'Checking…' : 'Check Capability'}
            </button>
          </div>
        </div>

        {result && v && (
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-4" data-testid="capability-result">
            <div className="flex items-center gap-3 mb-4">
              <v.Icon className={`w-6 h-6 ${result.verdict === 'BLOCKED' ? 'text-rose-600' : result.verdict === 'OK' ? 'text-emerald-600' : 'text-amber-600'}`} />
              <div>
                <span className="text-xs text-gray-500">Verdict</span>
                <div><StatusBadge tone={v.tone} testid="verdict-badge">{v.label}</StatusBadge></div>
              </div>
              <span className="ml-auto text-xs text-gray-400">{result.blocked_count} blocked · {result.verify_count} to verify</span>
            </div>
            <ul className="divide-y divide-gray-100">
              {result.checks.map((c, i) => (
                <li key={i} className="py-2 flex items-start justify-between gap-3" data-testid={`check-${c.name}`}>
                  <div className="min-w-0"><span className="text-sm font-medium text-gray-800">{c.name}</span>
                    <p className="text-xs text-gray-500 break-words">{c.detail}</p></div>
                  <StatusBadge tone={CHECK_TONE[c.status] || 'neutral'}>{c.status === 'OK' ? 'OK' : c.status === 'BLOCKED' ? 'Blocked' : 'Verify'}</StatusBadge>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-400 mt-3">{result.note}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
